import { generateUUIDv7 } from '../identity/uuidv7.js';
import { canonicalize } from '../security/rbac-contracts.js';
import type { SystemRole } from '../security/rbac-contracts.js';

export interface OwnerBinding {
  ownerId: string; // Monotonic UUIDv7
  keyId: string;
  publicKey: string; // Ed25519 public key hex
  algorithm: 'ed25519';
  signature: string;
}

export interface HnsBinding {
  name: string;
  meshId: string;
  ownerId: string;
  keyId: string;
  publicKey: string;
  resolverStatus: 'unverified' | 'verified_hns' | 'local_mesh';
  recordVersion: number;
  signature: string;
}

export interface GenesisConfiguration {
  schemaVersion: 1;
  genesisVersion: number;
  meshUuid: string;
  meshId: string;
  meshName: string;
  mission: string;
  owner: OwnerBinding;
  hns: {
    mesh: HnsBinding;
    owner: HnsBinding;
  };
  policyVersion: number;
  assignedRoles: readonly {
    principalId: string;
    role: SystemRole;
    scope: { kind: 'system' };
  }[];
  plugins: readonly { id: string; enabled: boolean }[];
  channels: readonly { id: string; name: string }[];
  theme: string;
  createdAt: number;
  signature: string;
}

/**
 * Pure TypeScript synchronous SHA-256 implementation.
 * Cross-platform compatible (Browser, Web Worker, Node.js, Electron Renderer).
 */
export function sha256Sync(str: string): string {
  const ch = (x: number, y: number, z: number) => (x & y) ^ (~x & z);
  const maj = (x: number, y: number, z: number) => (x & y) ^ (x & z) ^ (y & z);
  const sigma0 = (x: number) => ((x >>> 2) | (x << 30)) ^ ((x >>> 13) | (x << 19)) ^ ((x >>> 22) | (x << 10));
  const sigma1 = (x: number) => ((x >>> 6) | (x << 26)) ^ ((x >>> 11) | (x << 21)) ^ ((x >>> 25) | (x << 7));
  const gamma0 = (x: number) => ((x >>> 7) | (x << 25)) ^ ((x >>> 18) | (x << 14)) ^ (x >>> 3);
  const gamma1 = (x: number) => ((x >>> 17) | (x << 15)) ^ ((x >>> 19) | (x << 13)) ^ (x >>> 10);

  const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
  ];

  const words: number[] = [];
  const utf8 = unescape(encodeURIComponent(str));
  for (let i = 0; i < utf8.length; i++) {
    words[i >> 2] |= utf8.charCodeAt(i) << (24 - (i % 4) * 8);
  }
  const bitsLength = utf8.length * 8;
  words[bitsLength >> 5] |= 0x80 << (24 - (bitsLength % 32));
  words[(((bitsLength + 64) >> 9) << 4) + 15] = bitsLength;

  let H0 = 0x6a09e667;
  let H1 = 0xbb67ae85;
  let H2 = 0x3c6ef372;
  let H3 = 0xa54ff53a;
  let H4 = 0x510e527f;
  let H5 = 0x9b05688c;
  let H6 = 0x1f83d9ab;
  let H7 = 0x5be0cd19;

  for (let i = 0; i < words.length; i += 16) {
    const W = new Array(64);
    for (let t = 0; t < 16; t++) W[t] = words[i + t] | 0;
    for (let t = 16; t < 64; t++) W[t] = (gamma1(W[t - 2]) + W[t - 7] + gamma0(W[t - 15]) + W[t - 16]) | 0;

    let a = H0, b = H1, c = H2, d = H3, e = H4, f = H5, g = H6, h = H7;

    for (let t = 0; t < 64; t++) {
      const T1 = (h + sigma1(e) + ch(e, f, g) + K[t] + W[t]) | 0;
      const T2 = (sigma0(a) + maj(a, b, c)) | 0;
      h = g;
      g = f;
      f = e;
      e = (d + T1) | 0;
      d = c;
      c = b;
      b = a;
      a = (T1 + T2) | 0;
    }

    H0 = (H0 + a) | 0;
    H1 = (H1 + b) | 0;
    H2 = (H2 + c) | 0;
    H3 = (H3 + d) | 0;
    H4 = (H4 + e) | 0;
    H5 = (H5 + f) | 0;
    H6 = (H6 + g) | 0;
    H7 = (H7 + h) | 0;
  }

  const toHex = (n: number) => (n >>> 0).toString(16).padStart(8, '0');
  return toHex(H0) + toHex(H1) + toHex(H2) + toHex(H3) + toHex(H4) + toHex(H5) + toHex(H6) + toHex(H7);
}

export function deriveCryptographicMeshId(ownerPublicKey: string): string {
  const hash = sha256Sync(ownerPublicKey);
  return `mesh-${hash.substring(0, 16)}`;
}

export class GenesisProvisioner {
  static createGenesisConfiguration(params: {
    meshName: string;
    mission?: string;
    ownerAlias?: string;
    ownerPublicKey: string;
    ownerPrivateKeyPem?: string;
    existingGenesis?: GenesisConfiguration | null;
    nowMs?: number;
  }): GenesisConfiguration {
    const now = params.nowMs ?? Date.now();
    const isContinue = Boolean(params.existingGenesis);

    const meshUuid = isContinue ? params.existingGenesis!.meshUuid : generateUUIDv7(now);
    const ownerUuidv7 = isContinue ? params.existingGenesis!.owner.ownerId : generateUUIDv7(now);
    const genesisVersion = isContinue ? params.existingGenesis!.genesisVersion + 1 : 1;

    const sanitizedMeshName = params.meshName.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const meshId = deriveCryptographicMeshId(params.ownerPublicKey);
    const ownerAlias = params.ownerAlias || 'admin';
    const meshHnsDomain = `${sanitizedMeshName}`;
    const ownerHnsDomain = `${ownerAlias}.${sanitizedMeshName}`;

    const keyId = `key-ed25519-${ownerUuidv7.substring(0, 8)}`;

    const ownerPayload = {
      ownerId: ownerUuidv7,
      keyId,
      publicKey: params.ownerPublicKey,
      algorithm: 'ed25519' as const,
    };

    const ownerSig = `sig-owner-${canonicalize(ownerPayload).length}`;

    const ownerBinding: OwnerBinding = {
      ...ownerPayload,
      signature: ownerSig,
    };

    const meshHnsPayload = {
      name: meshHnsDomain,
      meshId,
      ownerId: ownerUuidv7,
      keyId,
      publicKey: params.ownerPublicKey,
      resolverStatus: 'unverified' as const,
      recordVersion: 1,
    };

    const ownerHnsPayload = {
      name: ownerHnsDomain,
      meshId,
      ownerId: ownerUuidv7,
      keyId,
      publicKey: params.ownerPublicKey,
      resolverStatus: 'unverified' as const,
      recordVersion: 1,
    };

    const hns = {
      mesh: { ...meshHnsPayload, signature: `sig-hns-${canonicalize(meshHnsPayload).length}` },
      owner: { ...ownerHnsPayload, signature: `sig-hns-${canonicalize(ownerHnsPayload).length}` },
    };

    const configPayload = {
      schemaVersion: 1 as const,
      genesisVersion,
      meshUuid,
      meshId,
      meshName: params.meshName,
      mission: params.mission || 'Decentralized sovereign peer collaboration mesh',
      owner: ownerBinding,
      hns,
      policyVersion: 1,
      assignedRoles: [
        {
          principalId: ownerUuidv7,
          role: 'owner' as SystemRole,
          scope: { kind: 'system' as const },
        },
      ],
      plugins: [
        { id: 'WhiteboardPlugin', enabled: true },
        { id: 'ChatPlugin', enabled: true },
        { id: 'RagKnowledgePlugin', enabled: true },
        { id: 'LLMWikiPlugin', enabled: true },
        { id: 'ScrumMasterPlugin', enabled: true },
        { id: 'BuzzAcpPlugin', enabled: true },
      ],
      channels: [
        { id: 'general', name: 'general' },
        { id: 'scrum', name: 'scrum' },
        { id: 'engineering-ops', name: 'engineering-ops' },
        { id: 'announcements', name: 'announcements' },
      ],
      theme: 'midnight',
      createdAt: now,
    };

    const genesisSig = `sig-genesis-v${genesisVersion}-${canonicalize(configPayload).length}`;

    return {
      ...configPayload,
      signature: genesisSig,
    };
  }
}
